import React, {useCallback, useContext, useState} from 'react';
import {AttentionMessageProps} from './index';
import CommentsList from '../../../../../src/components/CommentsList/CommentsList';
import Translate from '../../../../../src/components/Translate/Translate';
import CommentInline from '../../../../../src/components/CommentsList/CommentInline';
import {UserContext} from '../../../../../src/contexts/UserContext';
import classnames from '../../../../../src/helpers/classnames';
import ErrorBoundary from '../../../../../src/components/ErrorBoundary';
import {BootstrapColors} from '../../../../../src/@types/Bootstrap';
import RenderIf from '../../../../../src/components/RenderIf/RenderIf';
import NotificationException from '../../../../../src/exceptions/NotificationException';
import ValidationHttpException from '../../../../../src/exceptions/ValidationHttpException';
import {ICommentModel} from '../../../../../src/@interfaces/models/Comment/ICommentModel';

const pulseColorCssProps = {'--animation-pulse--color': 'var(--bs-danger-rgb)'} as React.CSSProperties;

type Props = Omit<AttentionMessageProps, 'setMessages' | 'catchHttpException'> & {
    message: string,
    component: React.ComponentType<AttentionMessageProps>
};

export type MessageComponentType = Omit<Props, 'component'>;

function hasMessageComponentGetter<T extends object>(item: T): item is T & {
    getMessageComponent: () => React.ComponentType<MessageComponentType>
} {
    return 'getMessageComponent' in item;
}

export default function ControlledOrderAttentionMessage({message, component: Component, ...props}: Props) {
    const userContext = useContext(UserContext);
    const [collapsed, setCollapsed] = useState(true);
    const [messages, setMessages] = useState<{
        color: BootstrapColors,
        message: string,
    }[]>([]);

    const catchHttpException = useCallback<AttentionMessageProps['catchHttpException']>(reason => {
        if (reason instanceof NotificationException) {
            setMessages(list => {
                return [{color: 'info', message: reason.message}, ...list];
            });

            return Promise.resolve();
        }

        if (reason instanceof ValidationHttpException) {
            setMessages(list => {
                const buffer: typeof list = [];
                for (const messages of Object.values(reason.getValidationErrors())) {
                    messages.forEach(message => {
                        buffer.push({color: 'danger', message});
                    });
                }

                return [...buffer, ...list];
            });

            return;
        }

        setMessages(list => {
            return [{color: 'danger', message: reason?.message ?? 'Something went wrong'}, ...list];
        });
    }, []);

    if (!(userContext && userContext.canAny([
        'orders@can-solve-order-deletion-requests',
        'orders@can-solve-order-deletion-requests-without-comment'
    ]))) {
        return null;
    }

    const isSolved = props.model.solved_at != null;
    const MessageComponent = hasMessageComponentGetter(Component) ?
            Component.getMessageComponent() :
            Translate;

    const hasReason = props.model?.comments?.some((comment: ICommentModel) => comment?.context?.is_reason === true);

    return <>
        <div>
            <button
                    type="button"
                    className={classnames('btn btn-sm me-2', {
                        'btn-outline-secondary': !collapsed || isSolved,
                        'btn-outline-danger with-pulse-animation': collapsed && !isSolved,
                    })}
                    style={pulseColorCssProps}
                    onClick={() => setCollapsed(v => !v)}
            >
                <i className={collapsed ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}/>
            </button>

            <button
                    type="button"
                    disabled={true}
                    className={classnames('me-2 btn btn-sm', {
                        'btn-outline-info': hasReason,
                        'btn-outline-secondary': !hasReason,
                    })}
            >
                <i className="fas fa-comment"/>
            </button>

            {props.model.state ? <span className="me-1">[<Translate
                    message={`OrderAttentionsField.states.${props.model.state}`}
                    className={classnames({
                        'text-danger': props.model.state === 'accepted',
                        'text-success': props.model.state === 'rejected',
                        'text-secondary': props.model.state === 'ignored',
                    })}
            />]</span> : null}

            <MessageComponent model={props.model} message={message}/>
        </div>

        <CommentsList
                list={props.model.comments}
                itemComponent={CommentInline}
                className={classnames('mt-2', {'d-none': collapsed})}
        />

        <RenderIf expression={messages.length}>
            <div className="mt-3">
                {messages.map((item, index) => {
                    return <div
                            key={`${item.message}-${index}`}
                            className={classnames('alert py-2 pe-2 d-flex', `alert-${item.color}`)}
                    >
                        <Translate message={item.message}/>

                        <button
                                type="button"
                                className="btn btn-sm btn-close ms-auto flex-grow-0"
                                onClick={() => {
                                    setMessages(list => {
                                        list.splice(index, 1);

                                        return [...list];
                                    });
                                }}
                        ></button>
                    </div>;
                })}
            </div>
        </RenderIf>

        {isSolved ? null : <div className={classnames('mt-3', {
            'd-none': collapsed
        })}>
            <ErrorBoundary>
                <Component
                        {...props}
                        setMessages={setMessages}
                        catchHttpException={catchHttpException}
                />
            </ErrorBoundary>
        </div>}
    </>;
}
